import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Plus, Sparkles, FileText, PenLine, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, MODES, LANGS, MARKETS, SPECIALTIES, can } from "@/lib/api";
import { StatusBadge, SampleBadge } from "@/components/Brand";
import BodyMap from "@/components/BodyMap";
import { inputCls } from "@/components/Fields";
import { IcdPicker, IcdLabel } from "@/components/IcdPicker";
import { useAuth } from "@/context/Auth";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const Sel = ({ value, onChange, options, testid, all = "All" }) => (
  <select data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)} className={inputCls}>
    <option value="">{all}</option>
    {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
  </select>
);

function NewProtocolDialog({ open, onOpenChange, treatments, bodies }) {
  const nav = useNavigate();
  const [busy, setBusy] = useState(false);
  const [meta, setMeta] = useState({ title: "", condition: "", body_codes: [], care_modes: ["at_home"], icd10: [] });
  const [brief, setBrief] = useState({ body_code: "", condition: "", treatment_type: "", population: "", care_mode: "at_home", market: "IN", phase_count: 3, notes: "" });
  const [text, setText] = useState("");
  const create = async (content, ai_paths = [], ai_draft_id = null, extra = {}) => {
    const body = { ...meta, ...extra, icd10: (extra.icd10 ?? meta.icd10).map((x) => x.code), content, ai_paths, ai_draft_id };
    const { data } = await api.post("/protocols", body);
    toast.success("Draft protocol created");
    nav(`/editor/${data.version_id}`);
  };
  const run = async (fn) => { setBusy(true); try { await fn(); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); } };
  const aiDraft = () => run(async () => {
    const { data } = await api.post("/ai/draft-protocol", brief);
    await create(data.content, data.ai_paths, data.draft_id, { title: meta.title || `${brief.condition} (draft by PranIQ AI)`, condition: brief.condition, body_codes: [brief.body_code], care_modes: [brief.care_mode], treatment_types: brief.treatment_type ? [brief.treatment_type] : [] });
  });
  const structure = () => run(async () => {
    const { data } = await api.post("/ai/structure-text", { text });
    await create(data.content, data.ai_paths, data.draft_id, { title: meta.title || "Imported clinician protocol" });
  });
  const file = async (f) => { if (f) setText(await f.text()); };
  const B = (k) => (e) => setBrief({ ...brief, [k]: e.target.value });
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl bg-white" data-testid="new-protocol-dialog">
        <DialogHeader><DialogTitle className="font-heading">New protocol</DialogTitle></DialogHeader>
        <input data-testid="new-protocol-title" className={inputCls} placeholder="Protocol title" value={meta.title} onChange={(e) => setMeta({ ...meta, title: e.target.value })} />
        <Tabs defaultValue="manual">
          <TabsList className="bg-slate-100">
            <TabsTrigger value="manual" data-testid="new-tab-manual"><PenLine size={13} className="mr-1" />Write by hand</TabsTrigger>
            <TabsTrigger value="ai" data-testid="new-tab-ai"><Sparkles size={13} className="mr-1" />Draft by PranIQ AI from brief</TabsTrigger>
            <TabsTrigger value="text" data-testid="new-tab-text"><FileText size={13} className="mr-1" />Structure my text</TabsTrigger>
          </TabsList>
          <TabsContent value="manual" className="space-y-2 pt-2">
            <input data-testid="new-protocol-condition" className={inputCls} placeholder="Condition / indication" value={meta.condition} onChange={(e) => setMeta({ ...meta, condition: e.target.value })} />
            <IcdPicker testid="new-protocol-icd10" value={meta.icd10} onChange={(icd10) => setMeta({ ...meta, icd10 })} />
            <Sel testid="new-protocol-body" value={meta.body_codes[0] || ""} onChange={(v) => setMeta({ ...meta, body_codes: v ? [v] : [] })} options={bodies} all="Body region…" />
            <button data-testid="create-manual-btn" disabled={busy || !meta.title} onClick={() => run(() => create(null))} className="rounded bg-sky-600 px-4 py-2 text-sm font-semibold text-white hover:bg-sky-700 disabled:opacity-50">Create empty draft</button>
          </TabsContent>
          <TabsContent value="ai" className="space-y-2 pt-2">
            <div className="grid sm:grid-cols-2 gap-2">
              <Sel testid="brief-body" value={brief.body_code} onChange={(v) => setBrief({ ...brief, body_code: v })} options={bodies} all="Body region…" />
              <input data-testid="brief-condition" className={inputCls} placeholder="Condition (e.g. Patellofemoral pain)" value={brief.condition} onChange={B("condition")} />
              <Sel testid="brief-treatment" value={brief.treatment_type} onChange={(v) => setBrief({ ...brief, treatment_type: v })} options={treatments} all="Treatment type (any)" />
              <input data-testid="brief-population" className={inputCls} placeholder="Population (e.g. adults 18–40)" value={brief.population} onChange={B("population")} />
              <Sel testid="brief-mode" value={brief.care_mode} onChange={(v) => setBrief({ ...brief, care_mode: v })} options={Object.entries(MODES)} />
              <Sel testid="brief-market" value={brief.market} onChange={(v) => setBrief({ ...brief, market: v })} options={Object.entries(MARKETS)} />
              <input data-testid="brief-phases" type="number" min={1} max={6} className={inputCls} value={brief.phase_count} onChange={(e) => setBrief({ ...brief, phase_count: Number(e.target.value) })} />
            </div>
            <textarea data-testid="brief-notes" className={inputCls} rows={2} placeholder="Notes for the assistant" value={brief.notes} onChange={B("notes")} />
            <p className="text-[11px] text-slate-500">The assistant drafts against the curated knowledge base only and leaves flagged placeholders where evidence is missing. Every field is marked “By PranIQ AI” and stays fully editable.</p>
            <button data-testid="ai-draft-btn" disabled={busy || !brief.condition} onClick={aiDraft} className="inline-flex items-center gap-2 rounded bg-violet-600 px-4 py-2 text-sm font-semibold text-white hover:bg-violet-700 disabled:opacity-50">
              {busy ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />} {busy ? "Drafting (can take ~1 min)…" : "Generate draft"}
            </button>
          </TabsContent>
          <TabsContent value="text" className="space-y-2 pt-2">
            <textarea data-testid="structure-text-input" className={inputCls} rows={8} placeholder="Paste or dictate your protocol text…" value={text} onChange={(e) => setText(e.target.value)} />
            <input data-testid="structure-file-input" type="file" accept=".txt,.md,.json,.csv" onChange={(e) => file(e.target.files[0])} className="text-xs" />
            <button data-testid="structure-btn" disabled={busy || text.length < 20} onClick={structure} className="inline-flex items-center gap-2 rounded bg-violet-600 px-4 py-2 text-sm font-semibold text-white hover:bg-violet-700 disabled:opacity-50">
              {busy ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />} Structure with AI
            </button>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}

function GenerateFromKeyword({ keyword, market, none }) {
  const nav = useNavigate();
  const [job, setJob] = useState(null);
  const [publish, setPublish] = useState(true);
  const timer = useRef(null);
  useEffect(() => () => clearInterval(timer.current), []);
  const stop = (msg) => { clearInterval(timer.current); setJob(null); if (msg) toast.error(msg); };
  const start = async () => {
    try {
      const { data } = await api.post("/ai/generate-from-keyword", { keyword, market: market || "IN", publish });
      setJob({ status: "running", stage: "Drafting protocol with AI" });
      const t0 = Date.now();
      clearInterval(timer.current);
      timer.current = setInterval(async () => {
        if (Date.now() - t0 > 6 * 60 * 1000) return stop("Generation is taking too long — please try again");
        try {
          const j = (await api.get(`/ai/jobs/${data.job_id}`)).data;
          setJob(j);
          if (j.status === "done") {
            clearInterval(timer.current);
            if (j.result.published) { toast.success("Published by PranIQ AI — not clinically reviewed. You can edit it as a new version."); nav(`/protocols/${j.result.protocol_id}`); } else { toast.success(j.result.publish_error ? `Draft created, not published: ${j.result.publish_error}` : "Draft by PranIQ AI created — review every field before submitting"); nav(`/editor/${j.result.version_id}`); }
          }
          if (j.status === "error") stop(j.error);
        } catch (e) { stop(errMsg(e)); }
      }, 3000);
    } catch (e) { toast.error(errMsg(e)); }
  };
  const running = job?.status === "running";
  return (
    <div data-testid="generate-from-keyword" className={`card-surface p-4 flex flex-wrap items-center gap-3 ${none ? "border-violet-300 bg-violet-50/40" : ""}`}>
      <Sparkles size={18} className="text-violet-600" />
      <div className="min-w-0 flex-1 basis-full sm:basis-0">
        <div className="text-[14px] font-semibold text-slate-900">{none ? `No protocol found for “${keyword}”.` : `Need a different protocol for “${keyword}”?`} Generate a complete protocol with PranIQ AI.</div>
        <div className="text-[12px] text-slate-500">Phases, criteria, safety, outcome measures, patient text, and new exercises with step-by-step pose illustrations.</div>
        <label className="mt-1.5 inline-flex items-center gap-2 text-[12px] font-semibold text-slate-700">
          <input data-testid="generate-publish-checkbox" type="checkbox" checked={publish} disabled={running} onChange={(e) => setPublish(e.target.checked)} />
          Publish straight away (no clinical review — marked “Not clinically reviewed”; edit later as a new version)
        </label>
      </div>
      <button data-testid="generate-from-keyword-btn" disabled={running} onClick={start} className="inline-flex items-center gap-2 rounded bg-violet-600 px-4 py-2 text-sm font-semibold text-white hover:bg-violet-700 disabled:opacity-60">
        {running ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}{running ? `${job.stage}…` : "Generate protocol"}
      </button>
    </div>
  );
}

export default function Library() {
  const { user } = useAuth();
  const [sp] = useSearchParams();
  const [f, setF] = useState({ q: sp.get("q") || "", body: "", treatment: "", mode: "", specialty: "", market: "", lang: "", phase: "", status: "" });
  const [rows, setRows] = useState([]);
  const [tax, setTax] = useState([]);
  const [open, setOpen] = useState(false);
  useEffect(() => { api.get("/taxonomy").then((r) => setTax(r.data)); }, []);
  useEffect(() => { setF((x) => ({ ...x, q: sp.get("q") || "" })); }, [sp]);
  useEffect(() => {
    const t = setTimeout(() => api.get("/protocols", { params: f }).then((r) => setRows(r.data)), 200);
    return () => clearTimeout(t);
  }, [f]);
  const set = (k) => (v) => setF({ ...f, [k]: v });
  const treatments = tax.filter((t) => t.axis === "treatment").map((t) => [t.code, `${t.parent ? "— " : ""}${t.name}`]);
  const bodies = tax.filter((t) => t.axis === "body").map((t) => [t.code, `${t.level === "joint" ? "— " : t.level === "structure" ? "—— " : ""}${t.name}`]);
  const tname = Object.fromEntries(tax.map((t) => [t.code, t.name]));
  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-end gap-3">
        <div>
          <p className="eyebrow">Dual taxonomy · body part × treatment</p>
          <h1 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900">Protocol Library</h1>
        </div>
        {can(user, "author") && (
          <button data-testid="new-protocol-btn" onClick={() => setOpen(true)} className="ml-auto inline-flex items-center gap-2 rounded bg-sky-600 px-4 py-2 text-sm font-semibold text-white hover:bg-sky-700">
            <Plus size={15} /> New protocol
          </button>
        )}
      </div>
      <div className="grid lg:grid-cols-[260px_1fr] gap-5">
        <aside className="card-surface p-4 space-y-3 h-fit order-2 lg:order-1" data-testid="library-filters">
          <BodyMap value={f.body} onChange={set("body")} />
          <input data-testid="filter-q" className={inputCls} placeholder="Search…" value={f.q} onChange={(e) => set("q")(e.target.value)} />
          <Sel testid="filter-body" value={f.body} onChange={set("body")} options={bodies} all="All body regions" />
          <Sel testid="filter-treatment" value={f.treatment} onChange={set("treatment")} options={treatments} all="All treatment types" />
          <Sel testid="filter-mode" value={f.mode} onChange={set("mode")} options={Object.entries(MODES)} all="All care modes" />
          <Sel testid="filter-specialty" value={f.specialty} onChange={set("specialty")} options={SPECIALTIES.map((s) => [s, s])} all="All specialties" />
          <Sel testid="filter-market" value={f.market} onChange={set("market")} options={Object.entries(MARKETS)} all="All markets" />
          <Sel testid="filter-lang" value={f.lang} onChange={set("lang")} options={Object.entries(LANGS)} all="Any language" />
          <Sel testid="filter-status" value={f.status} onChange={set("status")} options={["draft", "published", "retired"].map((s) => [s, s])} all="Any status" />
          <input data-testid="filter-phase" className={inputCls} placeholder="Phase contains… (e.g. strength)" value={f.phase} onChange={(e) => set("phase")(e.target.value)} />
          <button data-testid="filter-reset" onClick={() => setF({ q: "", body: "", treatment: "", mode: "", specialty: "", market: "", lang: "", phase: "", status: "" })} className="text-[12px] font-semibold text-sky-700 hover:underline">Reset filters</button>
        </aside>
        <div className="space-y-2 order-1 lg:order-2" data-testid="protocol-results">
          <p className="text-[12px] text-slate-500" data-testid="results-count">{rows.length} protocol(s)</p>
          {can(user, "ai") && f.q.trim().length >= 3 && <GenerateFromKeyword keyword={f.q.trim()} market={f.market} none={rows.length === 0} />}
          {rows.map((p, i) => (
            <Link key={p.id} to={`/protocols/${p.id}`} data-testid={`protocol-card-${p.slug}`} style={{ animationDelay: `${i * 30}ms` }}
              className="card-surface fade-in block p-4 hover:border-sky-300">
              <div className="flex flex-wrap items-center gap-2">
                <h3 className="font-heading text-[16px] font-bold text-slate-900">{p.title}</h3>
                {p.current_version && <StatusBadge status={p.current_version.status} testid={`card-status-${p.slug}`} />}
                {p.current_version && <span className="font-mono text-[11px] text-slate-500">v{p.current_version.version}</span>}
                {p.is_sample && <SampleBadge />}
              </div>
              <p className="mt-1 text-[13px] text-slate-600">{p.condition}{(p.icd10_labels || []).length > 0 && <> · {p.icd10_labels.map((x, k) => <span key={x.code}>{k > 0 && ", "}<IcdLabel x={x} /></span>)}</>}</p>
              <div className="mt-2 flex flex-wrap gap-1.5">
                {(p.body_codes || []).map((c) => <span key={c} className="rounded bg-cyan-50 px-1.5 py-0.5 text-[11px] text-teal-800">{tname[c] || c}</span>)}
                {(p.treatment_types || []).map((c) => <span key={c} className="rounded bg-slate-100 px-1.5 py-0.5 text-[11px] text-slate-700">{tname[c] || c}</span>)}
                {(p.care_modes || []).map((m) => <span key={m} className="rounded border border-slate-200 px-1.5 py-0.5 text-[11px] text-slate-600">{MODES[m]}</span>)}
                {(p.current_version?.approved_markets || []).map((m) => <span key={m} className="rounded border border-sky-200 px-1.5 py-0.5 text-[11px] font-semibold text-sky-700">{m}</span>)}
              </div>
            </Link>
          ))}
        </div>
      </div>
      <NewProtocolDialog open={open} onOpenChange={setOpen} treatments={treatments} bodies={bodies} />
    </div>
  );
}
